import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PencilIcon } from 'lucide-react';
import { useProfileStore } from '../../stores/profileStore';

interface ProfileAvatarUploadProps {
  profileId: string;
  imageUrl?: string | null;
  onChange?: (url: string) => void;
  size?: number; // px
  design?: 'form' | 'settings';
  className?: string;
}

export const ProfileAvatarUpload: React.FC<ProfileAvatarUploadProps> = ({
  profileId,
  imageUrl,
  onChange,
  size = 96,
  design = 'form',
  className = '',
}) => {
  const { t } = useTranslation(['auth', 'common']);
  const { uploadAvatar } = useProfileStore();

  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cacheBuster, setCacheBuster] = useState('');

  const avatarSize = typeof size === 'number' ? `${size}px` : size;
  const inputId = `avatar-upload-${profileId}`;

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const publicUrl = await uploadAvatar(profileId, file);
      if (onChange) {
        onChange(publicUrl);
      }
      setCacheBuster(`?t=${Date.now()}`);
    } catch (error: unknown) {
      console.error('Error uploading avatar:', error);
      if (
        typeof error === 'object' &&
        error !== null &&
        'error' in error &&
        (error.error as string).toLowerCase() === 'payload too large'
      ) {
        setError(t('error.imageTooLarge', { ns: 'common' }));
      } else {
        setError(t('error.uploadImage', { ns: 'common' }));
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 items-center md:items-start">
      <div
        className={`relative flex flex-col items-center justify-center bg-[#E1E9F0] rounded-full w-full h-full ${className}`}
        style={design === 'settings' ? { width: avatarSize, height: avatarSize } : undefined}
      >
        <label
          htmlFor={inputId}
          className="cursor-pointer flex flex-col items-center justify-center w-full h-full"
        >
          {imageUrl ? (
            <img
              src={`${imageUrl}${cacheBuster}`}
              alt={t('completeProfileStep.profilePreviewAlt')}
              className={`${design === 'settings' ? 'rounded-full' : ''} object-cover w-full h-full`}
              style={
                design === 'form'
                  ? { width: '100%', height: '100%' }
                  : { width: avatarSize, height: avatarSize }
              }
            />
          ) : (
            <>
              <span className="text-secondary font-ambit text-[0.9375rem] not-italic font-normal leading-[1.25rem] mb-2">
                {t('completeProfileStep.chooseImage')}
              </span>
              <span className="text-secondary font-ambit text-[0.625rem] not-italic font-normal leading-[0.9375rem] px-2 text-center">
                {t('completeProfileStep.imageHint')}
              </span>
            </>
          )}
          <input
            id={inputId}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
            className="hidden"
            onChange={handleFileChange}
          />
          {uploading && (
            <span className="absolute left-1/2 top-full -translate-x-1/2 mt-2 text-xs text-secondary-light">
              {t('completeProfileStep.uploading')}
            </span>
          )}
          {design === 'settings' && (
            <span className="absolute bottom-[1.5rem] right-[0]">
              <div className="bg-primary w-10 h-10 rounded-full flex items-center justify-center shadow-xl">
                <PencilIcon className="w-5 h-5 text-white" />
              </div>
            </span>
          )}
        </label>
      </div>
      {error && <span className="text-[#F04438] text-[0.9375rem] font-ambit mt-1">{error}</span>}
    </div>
  );
};
